import React, { useEffect, useRef, useState } from 'react';
import { Route, Redirect, Switch, useLocation, useHistory } from 'react-router';
import MenuLayout from 'core/layout/MenuLayout';
import PrivateRoute from 'core/routes/PrivateRoute';
import Login from 'features/shared/login/Login';
import Dashboard from 'features/full-service/dashboard/Dashboard';
import Tutorial from 'features/full-service/tutorial/Tutorial';
import InitialApppointment from 'features/full-service/appointments/initial/InitialAppointment';
import Account from 'features/shared/account/Account';
import AppointmentDetails from 'features/full-service/appointments/AppointmentDetails';
import Checkpoint from 'features/shared/login/Checkpoint';
import Update from 'features/shared/update/Update';
import ForgotPasswordReset from 'features/shared/login/ForgotPasswordReset';
import IntegrationReset from 'features/shared/login/IntegrationReset';
import PasswordReset from 'features/shared/login/PasswordReset';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { useSelector } from 'react-redux';
import { RootState } from 'core/store';
import {
  DIGITAL_PAGES,
  FULL_SERVICE_PAGES,
  INVENTORY_COLORS_PRODUCT_GROUP_ID,
  INVENTORY_HOME_SELECTIONS_PATH
} from 'core/constants';
import DigitalDashboard from 'features/digital/views/dashboard/DigitalDashboard';
import Contacts from 'features/shared/contacts/Contacts';
import DigitalCatalog from 'features/digital/views/catalog/DigitalCatalog';
import ContactDetails from 'features/shared/contacts/ContactDetails';
import InventoryGroups from 'features/shared/inventory/InventoryGroups';
import InventoryHomeSelections from 'features/shared/inventory/selections/InventoryHomeSelections';
import InventoryProducts from 'features/shared/inventory/InventoryProducts';
import DocumentsGroups from 'features/shared/documents/DocumentsGroups';
import DocumentsList from 'features/shared/documents/DocumentsList';
// import ConstructionOverview from 'features/shared/construction/ConstructionOverview';
import DocumentsConstructionSubfolders from 'features/shared/documents/DocumentsConstructionSubfolders';
import InventoryDetailsDesktop from 'features/shared/inventory/InventoryDetailsDesktop';
import Renew from 'features/shared/billing/Renew';
import Confirmation from 'features/shared/billing/Confirmation';
import CheckoutDesktop from 'features/shared/billing/CheckoutDesktop';
import NotFound from 'core/layout/NotFound';
import DocumentEdit from 'features/shared/documents/DocumentEdit';
import OnboardingQuestion1 from 'features/shared/signup/views/digital-signup/onboarding/OnboardingQuestion1';
import OnboardingQuestion3 from 'features/shared/signup/views/digital-signup/onboarding/OnboardingQuestion3';
import OnboardingQuestion4 from 'features/shared/signup/views/digital-signup/onboarding/OnboardingQuestion4';
import OnboardingQuestion5 from 'features/shared/signup/views/digital-signup/onboarding/OnboardingQuestion5';

const Routes: React.FC = () => {
  let location = useLocation();
  let history = useHistory();

  const [lastPathIndex, setLastPathIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState('left');

  const { isDesktopWidth } = useSelector((state: RootState) => state.platform);
  const { isDigital } = useSelector((state: RootState) => state.user);
  const { isExpired } = useSelector((state: RootState) => state.home);
  const isAuthenticated = useSelector(
    (state: RootState) => state.login.loggedIn
  );

  const nodeRef = useRef(null);

  const getCurrentPathIndex = () => {
    let currentPathIndex = 0;
    if (!isDigital) {
      FULL_SERVICE_PAGES.forEach((page, i) => {
        if (location.pathname.match(page.path)) {
          currentPathIndex = i;
        }
      });
    } else {
      DIGITAL_PAGES.forEach((page, i) => {
        if (location.pathname.match(page.path)) {
          currentPathIndex = i;
        }
      });
    }

    return currentPathIndex;
  };

  const getLastPathIndex = () => {
    if (!isDigital) {
      FULL_SERVICE_PAGES.forEach((page, i) => {
        if (location.pathname.match(page.path)) {
          setLastPathIndex(i);
        }
      });
    } else {
      DIGITAL_PAGES.forEach((page, i) => {
        if (location.pathname.match(page.path)) {
          setLastPathIndex(i);
        }
      });
    }
  };

  const checkIfDigitalExpired = () => {
    if (
      isDigital &&
      isExpired &&
      location.pathname !== '/renew' &&
      location.pathname !== '/renew/checkout' &&
      location.pathname !== '/account' &&
      location.pathname !== '/renew/confirmation'
    ) {
      history.push(`/renew`);
    }
  };

  useEffect(() => {
    getLastPathIndex();
    setSlideDirection(getCurrentPathIndex() > lastPathIndex ? 'left' : 'right');
    checkIfDigitalExpired();
  }, [location]);

  function FullServiceAppRoutes() {
    return (
      <Switch location={location}>
        <PrivateRoute path="/checkpoint">
          <MenuLayout>
            <Checkpoint />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute path="/dashboard">
          <MenuLayout>
            <Dashboard />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/initial">
          <MenuLayout>
            <InitialApppointment />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/account">
          <MenuLayout>
            <Account />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/appointment/:id">
          <MenuLayout>
            <AppointmentDetails />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/inventory">
          <MenuLayout>
            <InventoryGroups />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path={INVENTORY_HOME_SELECTIONS_PATH}>
          <MenuLayout>
            <InventoryHomeSelections />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute
          exact
          path={`/inventory/group/${INVENTORY_COLORS_PRODUCT_GROUP_ID}`}
        >
          <MenuLayout>
            <InventoryHomeSelections />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/inventory/group/:id">
          <MenuLayout>
            <InventoryProducts />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/inventory/group/:groupId/product/:productId">
          <MenuLayout>
            <InventoryDetailsDesktop />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/contacts">
          <MenuLayout>
            <Contacts />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/contacts/:id">
          <MenuLayout>
            <ContactDetails />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/documents">
          <MenuLayout>
            <DocumentsGroups />
          </MenuLayout>
        </PrivateRoute>
        {/* <PrivateRoute exact path="/home-construction">
          <MenuLayout>
            <ConstructionOverview />
          </MenuLayout>
        </PrivateRoute> */}
        <PrivateRoute exact path="/documents/group/construction">
          <MenuLayout>
            <DocumentsConstructionSubfolders />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/documents/group/construction/:type">
          <MenuLayout>
            <DocumentsList />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/documents/group/construction/:type/:id">
          <MenuLayout>
            <DocumentEdit />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/documents/group/:type">
          <MenuLayout>
            <DocumentsList />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/documents/group/:type/:id">
          <MenuLayout>
            <DocumentEdit />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute path="/renew/confirmation">
          <MenuLayout>
            <Confirmation />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute path="/renew/checkout">
          <MenuLayout>
            <CheckoutDesktop />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/renew">
          <MenuLayout>
            <Renew />
          </MenuLayout>
        </PrivateRoute>
        <Route path="*">
          <Route component={NotFound} />
        </Route>
      </Switch>
    );
  }

  function DigitalRoutes() {
    return (
      <Switch>
        <PrivateRoute path="/checkpoint">
          <MenuLayout>
            <Checkpoint />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute path="/dashboard">
          <MenuLayout>
            <DigitalDashboard />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/onboarding/what-type-of-home-do-you-own">
          <MenuLayout>
            <OnboardingQuestion1 />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/onboarding/what-are-you-looking-to-get-out-of-this-app"
        >
          <MenuLayout>
            <OnboardingQuestion3 />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/onboarding/what-type-of-homeowner-are-you">
          <MenuLayout>
            <OnboardingQuestion4 />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/onboarding/how-long-do-you-expect-to-stay">
          <MenuLayout>
            <OnboardingQuestion5 />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/account">
          <MenuLayout>
            <Account />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/catalog">
          <MenuLayout>
            <DigitalCatalog />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/inventory">
          <MenuLayout>
            <InventoryGroups />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path={INVENTORY_HOME_SELECTIONS_PATH}>
          <MenuLayout>
            <InventoryHomeSelections />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute
          exact
          path={`/inventory/group/${INVENTORY_COLORS_PRODUCT_GROUP_ID}`}
        >
          <MenuLayout>
            <InventoryHomeSelections />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/inventory/group/:id">
          <MenuLayout>
            <InventoryProducts />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/inventory/group/:groupId/product/:productId">
          <MenuLayout>
            <InventoryDetailsDesktop />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/documents">
          <MenuLayout>
            <DocumentsGroups />
          </MenuLayout>
        </PrivateRoute>
        {/* <PrivateRoute exact path="/home-construction">
          <MenuLayout>
            <ConstructionOverview />
          </MenuLayout>
        </PrivateRoute> */}
        <PrivateRoute exact path="/documents/group/construction">
          <MenuLayout>
            <DocumentsConstructionSubfolders />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/documents/group/construction/:type">
          <MenuLayout>
            <DocumentsList />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/documents/group/construction/:type/:id">
          <MenuLayout>
            <DocumentEdit />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/documents/group/:type">
          <MenuLayout>
            <DocumentsList />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/documents/group/:type/:id">
          <MenuLayout>
            <DocumentEdit />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/contacts">
          <MenuLayout>
            <Contacts />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/contacts/:id">
          <MenuLayout>
            <ContactDetails />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute path="/renew/confirmation">
          <MenuLayout>
            <Confirmation />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute path="/renew/checkout">
          <MenuLayout>
            <CheckoutDesktop />
          </MenuLayout>
        </PrivateRoute>
        <PrivateRoute exact path="/renew">
          <MenuLayout>
            <Renew />
          </MenuLayout>
        </PrivateRoute>
        <Route path="*">
          <Route component={NotFound} />
        </Route>
      </Switch>
    );
  }

  // AGENT NOTE: RouteList is declared inside Routes, so every Routes re-render remounts
  // the whole route tree, and each PrivateRoute mount force-refreshes the Firebase token.
  // Never subscribe Routes to extra Redux state; mount app-wide hooks outside Routes (see App.tsx).
  function RouteList() {
    return (
      <Switch location={location}>
        <Route exact path="/">
          <Redirect to="/login" />
        </Route>
        <Route path="/update" component={Update} />
        <Route path="/login" component={Login} />
        <Route path="/forgot-password" component={ForgotPasswordReset} />
        <Route path="/password/reset" component={PasswordReset} />
        <Route path="/integration/reset" component={IntegrationReset} />
        <Route path="/auth/:code" component={Login} />
        <Route path="/tutorial">
          <MenuLayout>
            <Tutorial />
          </MenuLayout>
        </Route>
        {/* Firebase OAuth redirect handler — required for sign-in to complete */}
        {/* <Route path="/__/auth/handler" component={() => null} /> */}

        <PrivateRoute path="/checkpoint">
          <Checkpoint />
        </PrivateRoute>
        <Route
          path="*"
          render={() => {
            if (!isAuthenticated) {
              return (
                <>
                  {FullServiceAppRoutes()}
                  {DigitalRoutes()}
                </>
              );
            } else {
              return isAuthenticated && !isDigital
                ? FullServiceAppRoutes()
                : DigitalRoutes();
            }
          }}
        />
        <Route component={NotFound} />
      </Switch>
    );
  }

  return isDesktopWidth ? (
    <SwitchTransition>
      <CSSTransition
        key={location.pathname}
        classNames="fade"
        timeout={300}
        nodeRef={nodeRef}
      >
        <div ref={nodeRef} className={slideDirection}>
          <RouteList />
        </div>
      </CSSTransition>
    </SwitchTransition>
  ) : (
    <div
      className={
        history.action === 'POP' || (location.state as any)?.slideBack
          ? 'slide-back-context'
          : ''
      }
    >
      <SwitchTransition>
        <CSSTransition
          key={location.pathname}
          classNames="slide"
          timeout={300}
          nodeRef={nodeRef}
        >
          <div ref={nodeRef}>
            <RouteList />
          </div>
        </CSSTransition>
      </SwitchTransition>
    </div>
  );
};

export default Routes;
